import { AbstractModel } from '@/models/base'
import type { IOrganization } from '@/models/organization'
import { Organization } from '@/models/organization'
import type { IClickThroughAgreementStatus, IUser, IUserData, IUserProfile, IUserProfileData } from './user.types'

export class UserProfile extends AbstractModel implements IUserProfile {
  readonly id?: number
  readonly about: string
  readonly country: string
  readonly business_title: string
  readonly job_description: string

  constructor(data: IUserProfileData) {
    super()

    // Validate data
    const requiredFields: (keyof IUserProfileData)[] = ['about', 'country', 'business_title', 'job_description']
    this.validate(data, requiredFields)

    this.about = data.about!
    this.country = data.country!
    this.business_title = data.business_title!
    this.job_description = data.job_description!

    if (data.id) {
      this.id = data.id
    }
  }
}

/**
 * A class representing a User.
 */
export class User extends AbstractModel implements IUser {
  readonly id?: number
  readonly first_name: string
  readonly last_name: string = ''
  readonly email: string
  readonly is_active: boolean = false
  readonly last_login?: Date
  readonly profile: IUserProfile | null = null
  readonly organization: IOrganization | null = null
  readonly click_through_agreement: IClickThroughAgreementStatus | null = null

  constructor(data: IUserData) {
    super()

    // Validate data
    const requiredFields: (keyof IUserData)[] = ['email', 'first_name']
    this.validate(data, requiredFields)

    // Initialize object assuming data valid
    this.first_name = data.first_name!
    this.email = data.email!

    if (data.id) {
      this.id = data.id
    }

    if (data.last_name) {
      this.last_name = data.last_name
    }

    if (data.is_active) {
      this.is_active = data.is_active
    }

    if (data.profile) {
      this.profile = new UserProfile(data.profile)
    }

    if (data.organization) {
      this.organization = new Organization(data.organization)
    }

    if (data.last_login) {
      this.last_login =
        typeof data.last_login === 'string' ? new Date(data.last_login) : data.last_login
    }

    if (data.click_through_agreement) {
      this.click_through_agreement = {
        required: !!data.click_through_agreement.required,
        accepted: !!data.click_through_agreement.accepted,
        version: data.click_through_agreement.version
      }
    }
  }

  // Returns the user's first and last name
  getFullName(): string {
    return `${this.first_name} ${this.last_name}`
  }

  // Returns the user's name initials
  getInitials(): string {
    const firstInitial = this.first_name[0]
    const lastInitial = this.last_name ? this.last_name[0] : ''

    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  // Returns user's name as string representation of object
  toString(): string {
    return this.getFullName()
  }
}
